/**
 * FinanceApp - Frontend React
 * Plateforme de pilotage tout-en-un pour dirigeants de PME
 *
 * Copyright (c) 2026 Emmanuel TESTE
 * Tous droits réservés - Logiciel propriétaire
 */
import { useState, useEffect, useRef } from "react";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ToastProvider, useToast } from "./contexts/ToastContext";
import TrialBanner from "./components/TrialBanner";
import DemoDataBanner from "./components/DemoDataBanner";
import api, { setToken, getToken } from "./services/api";
import OnboardingWizard from "./components/onboarding/OnboardingWizard";
import ActivityQuestionnaire from "./components/onboarding/ActivityQuestionnaire";
import Login from "./pages/Login";
import Sidebar from "./components/Sidebar";
import Topbar from "./components/Topbar";
import NewVersionBanner from "./components/NewVersionBanner";
import Dashboard from "./pages/Dashboard";
import Groupe from "./pages/Groupe";
import Analytics from "./pages/Analytics";
import Forecast from "./pages/Forecast";
import Facturation from "./pages/Facturation";
import Demarrage from "./pages/Demarrage";
import MonMoisComptable from "./pages/MonMoisComptable";
import SyntheseTVA from "./pages/SyntheseTVA";
import FactElec from "./pages/FactElec";
import Paiements from "./pages/Paiements";
import Achats from "./pages/Achats";
import Tresorerie from "./pages/Tresorerie";
import Banque from "./pages/Banque";
import Integrations from "./pages/Integrations";
import NoteDeFrais from "./pages/NoteDeFrais";
import Nouveautes from "./pages/Nouveautes";
import Aide from "./pages/Aide";
import TVA from "./pages/TVA";
import Relances from "./pages/Relances";
import Comptabilite from "./pages/Comptabilite";
import PlanComptable from "./pages/PlanComptable";
import FEC from "./pages/FEC";
import ExpertComptable from "./pages/ExpertComptable";
import Securite from "./pages/Securite";
import Equipe from "./pages/Equipe";
import Modules from "./pages/Modules";
import Societes from "./pages/Societes";
import Stocks from "./pages/Stocks";
import Abonnements     from "./pages/Abonnements";
import Immobilisations from "./pages/Immobilisations";
import Budget          from "./pages/Budget";
import CRM from "./pages/CRM";
import Clients from "./pages/Clients";
import ProspectionIA from "./pages/ProspectionIA";
import ComptaAnalytique from "./pages/ComptaAnalytique";
import MonAbonnement from "./pages/MonAbonnement";
import DemoAccess from "./pages/DemoAccess";
import ExpertAccess from "./pages/ExpertAccess";
import Landing from "./pages/Landing";
import Register from "./pages/Register";
import MigrationDashboard from "./pages/MigrationDashboard";
import Rattrapage from "./pages/Rattrapage";
import Fusion from "./pages/Fusion";
import PortalClient from "./pages/PortalClient";
import Superadmin from "./pages/Superadmin";
import DocumentsMultilingues from "./pages/DocumentsMultilingues";
import VeilleReglementaire from "./pages/VeilleReglementaire";
import DocumentsArchive from "./pages/DocumentsArchive";
import Commandes from "./pages/Commandes";
import Agenda from "./pages/Agenda";
import { AIProvider } from "./contexts/AIContext";
import AIDrawer from "./components/AIDrawer";
import Booking from "./pages/Booking";
import Ecommerce from "./pages/Ecommerce";
import "./App.css";
import "./styles/global.css";
import Footer from "./components/Footer";
import CookieBanner from "./components/CookieBanner";
import Tarifs from "./pages/Tarifs";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import MentionsLegales from "./pages/MentionsLegales";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import { ThemeProvider } from "./contexts/ThemeContext";
import { Zap } from "lucide-react";

const PAGES = {
  dashboard:     { label: "Tableau de bord",          component: Dashboard },
  demarrage:     { label: "Démarrage",                 component: Demarrage },
  monmois:       { label: "Mon mois comptable",        component: MonMoisComptable },
  synthesetva:   { label: "Synthèse TVA",              component: SyntheseTVA },
  groupe:        { label: "Vue groupe",                component: Groupe },
  analytics:     { label: "Analytique avancé",         component: Analytics },
  forecast:      { label: "Prévision trésorerie",      component: Forecast },
  facturation:   { label: "Facturation",               component: Facturation },
  factelec:      { label: "Facturation électronique",  component: FactElec },
  paiements:     { label: "Paiements",                 component: Paiements },
  achats:        { label: "Achats",                    component: Achats },
  tresorerie:    { label: "Trésorerie",                component: Tresorerie },
  banque:        { label: "Comptes bancaires",         component: Banque },
  integrations:  { label: "Intégrations",              component: Integrations },
  ndf:           { label: "Notes de frais",            component: NoteDeFrais },
  nouveautes:    { label: "Nouveautés",                component: Nouveautes },
  aide:          { label: "Aide",                      component: Aide },
  tva:           { label: "TVA intracommunautaire",    component: TVA },
  relances:      { label: "Relances automatiques",     component: Relances },
  comptabilite:  { label: "Comptabilité",              component: Comptabilite },
  plancomptable: { label: "Plan comptable",            component: PlanComptable },
  fec:           { label: "FEC & exports",             component: FEC },
  expertcomptable:{ label: "Accès expert-comptable",  component: ExpertComptable },
  securite:      { label: "Sécurité & accès",          component: Securite },
  equipe:        { label: "Gestion d'équipe",           component: Equipe },
  modules:       { label: "Modules & config",          component: Modules },
  societes:      { label: "Gestion des sociétés",      component: Societes },
  stocks:        { label: "Gestion des stocks",        component: Stocks },
  abonnements:      { label: "Abonnements récurrents",    component: Abonnements },
  immobilisations:  { label: "Immobilisations",            component: Immobilisations },
  budget:           { label: "Budget & Prévisionnel",      component: Budget },
  clients:           { label: "Clients",                      component: Clients },
  crm:               { label: "CRM",                         component: CRM },
  prospectionia:     { label: "Prospection IA",              component: ProspectionIA },
  comptaanalytique:  { label: "Comptabilité Analytique",     component: ComptaAnalytique },
  monabonnement:     { label: "Mon abonnement",              component: MonAbonnement },
  migration:         { label: "Tableau de migration",        component: MigrationDashboard },
  rattrapage:        { label: "Rattrapage comptable 2026",   component: Rattrapage },
  fusion:            { label: "Fusion de sociétés",          component: Fusion },
  portal:            { label: "Portail client",              component: PortalClient },
  multilingues:      { label: "Documents Multilingues",      component: DocumentsMultilingues },
  veillereglem:      { label: "Veille Réglementaire",         component: VeilleReglementaire },
  documentsarchive:  { label: "Espace documentaire & GED",    component: DocumentsArchive },
  commandes:         { label: "Commandes clients",              component: Commandes },
  agenda:            { label: "Agenda & Prise de RDV",          component: Agenda },
  ecommerce:         { label: "E-commerce",                      component: Ecommerce },
  tarifs:            { label: "Tarifs & abonnement", component: Tarifs },
};

// ── Contenu principal (nécessite d'être authentifié) ──────────
function AppContent() {
  const { user, logout, loading: authLoading, refreshUser } = useAuth();
  const toast = useToast();

  // Retour de Stripe Checkout niveau 1 (server/routes/billing.js POST /checkout-session)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const result = params.get('platform_checkout');
    if (result === 'success') {
      toast.success("Paiement en cours de confirmation — l'accès complet sera rétabli sous quelques instants.");
      refreshUser();
    } else if (result === 'cancel') {
      toast.info('Paiement annulé.');
    }
    if (result) {
      params.delete('platform_checkout');
      const newSearch = params.toString();
      window.history.replaceState({}, '', window.location.pathname + (newSearch ? '?' + newSearch : ''));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [currentPage, setCurrentPage]   = useState(() => {
    if (window.location.hash === '#landing') return '__landing';
    if (window.location.hash === '#register') return '__register';
    // /booking doit être vérifié AVANT /portal : les liens "Replanifier"/"Annuler"
    // des emails de RDV contiennent aussi "token=" dans leur URL, et étaient donc
    // interceptés à tort par la condition __portal ci-dessous (routage vers un
    // écran nécessitant une connexion, alors que la réservation est publique).
    if (window.location.pathname.startsWith('/booking')) return '__booking';
    // Même piège que /booking ci-dessus : le token est ici dans le PATH
    // (/demo/<token>), pas le query string, mais vérifié avant /portal par
    // cohérence avec les autres routes publiques à token de cette liste.
    if (window.location.pathname.startsWith('/demo/')) return '__demo-access';
    // Même piège que /booking ci-dessus : le lien d'invitation expert-comptable
    // (généré par server/routes/externalAccess.js POST /) contient "token="
    // dans son query string et serait sinon intercepté à tort par la
    // condition __portal juste en dessous.
    if (window.location.pathname === '/expert-access') return '__expert-access';
    // Même piège que /booking ci-dessus : cette URL contient aussi "token="
    // dans son query string, et serait sinon interceptée à tort par la
    // condition __portal juste en dessous.
    if (window.location.pathname === '/reset-password') return '__reset-password';
    if (window.location.pathname === '/mot-de-passe-oublie') return '__forgot-password';
    if (window.location.pathname === '/portal' || window.location.search.includes('token=')) return '__portal';
    if (window.location.pathname === '/superadmin' || window.location.hash === '#superadmin') return '__superadmin';
    if (window.location.hash === '#abonnements') return 'abonnements';
    // Pages légales — publiques, accessibles sans authentification, URL
    // directe partageable (footer landing + footer application).
    if (window.location.pathname === '/mentions-legales') return '__mentions-legales';
    if (window.location.pathname === '/confidentialite')  return '__confidentialite';
    if (window.location.pathname === '/cgu')               return '__cgu';
    return "__landing";
  });
  const [sidebarOpen, setSidebarOpen]   = useState(true);
  const [companies, setCompanies]       = useState([]);
  const [currentCompany, setCurrentCompany] = useState(null);
  // Nouvel abonné (essai) : « Démarrage » s'ouvre d'office, une fois par session
  useEffect(() => {
    if (!currentCompany?.id || currentCompany.status !== 'trial' || currentPage !== 'dashboard') return;
    const key = `execpro360.demarrage.shown.${currentCompany.id}`;
    try { if (sessionStorage.getItem(key)) return; sessionStorage.setItem(key, '1'); } catch { return; }
    setCurrentPage('demarrage');
  }, [currentCompany?.id, currentCompany?.status, currentPage]);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const [switchingCompany, setSwitchingCompany] = useState(false);
  const switchRequestRef = useRef(0);
  const [countBadges, setCountBadges] = useState({});
  // Incrémenté après suppression des données d'exemple : remonte la page
  // courante pour recharger ses chiffres.
  const [dataVersion, setDataVersion] = useState(0);

  // Chargement des sociétés — extrait en fonction réutilisable pour pouvoir
  // la redéclencher après le questionnaire activité/priorités (partie 1 de
  // l'onboarding personnalisé), qui doit rafraîchir currentCompany.activity_type
  // sans attendre un changement de `user`.
  // Société active = celle du jeton (company_id), pas la première de la
  // liste : après une bascule vers une satellite puis un rechargement, la
  // liste contient aussi la société mère, et afficher data[0] ferait
  // diverger la société affichée de celle sur laquelle l'API travaille.
  const tokenCompanyId = () => {
    try { return JSON.parse(atob(getToken().split(".")[1].replace(/-/g, "+").replace(/_/g, "/"))).company_id ?? null; }
    catch { return null; }
  };
  const loadCompanies = () => {
    if (!user) return;
    setCompaniesLoading(true);
    return api.companies.list()
      .then(data => {
        setCompanies(data);
        if (data.length > 0) {
          const activeId = tokenCompanyId();
          setCurrentCompany(data.find(c => c.id === activeId) || data[0]);
        }
      })
      .catch(console.error)
      .finally(() => setCompaniesLoading(false));
  };

  useEffect(() => { loadCompanies(); }, [user]); // eslint-disable-line

  // Compteurs du menu (29/10/2026) : factures de vente et d'achat à valider,
  // rechargés à chaque changement de société ou de page
  useEffect(() => {
    if (!currentCompany?.id) { setCountBadges({}); return; }
    api.demarrage.get(currentCompany.id)
      .then(d => setCountBadges({
        facturation: { count: d?.counts?.sales_pending || 0, title: `${d?.counts?.sales_pending || 0} facture(s) de vente à valider (onglet « À valider »)` },
        achats: { count: d?.counts?.purchases_pending || 0, title: `${d?.counts?.purchases_pending || 0} facture(s) d'achat à valider` },
      }))
      .catch(() => setCountBadges({}));
  }, [currentCompany?.id, currentPage]);

  // Changement de société active : appelle le backend pour ré-émettre un token
  // avec le bon company_id (les créations/écritures dépendent de ce token, pas
  // du state React local — cf. server/routes/auth.js switch-company).
  const handleCompanyChange = async (company) => {
    if (!company || company.id === currentCompany?.id || switchingCompany) return;
    const requestId = ++switchRequestRef.current;
    setSwitchingCompany(true);
    try {
      const res = await api.auth.switchCompany(company.id);
      if (requestId !== switchRequestRef.current) return; // réponse obsolète, ignorée
      setToken(res.token);
      setCurrentCompany(res.company);
      setCurrentPage("dashboard");
    } catch (err) {
      console.error("Erreur switch-company:", err);
      window.alert(err.message || "Impossible de changer de société active.");
    } finally {
      if (requestId === switchRequestRef.current) setSwitchingCompany(false);
    }
  };

  // Écran de chargement initial
  if (authLoading) {
    return (
      <div style={{
        minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center",
        background: "var(--bg-primary)", flexDirection: "column", gap: 12,
      }}>
        <Zap size={32} color="var(--accent)" strokeWidth={1.5} />
        <div style={{ color: "var(--text-muted)", fontSize: 14 }}>Chargement de FinanceApp…</div>
      </div>
    );
  }

  // Page publique de réservation — accessible sans auth
  if (currentPage === '__booking') {
    return <Booking />;
  }

  // Démo bac à sable — lien à durée limitée, accessible sans auth principale
  if (currentPage === '__demo-access') {
    return <DemoAccess token={window.location.pathname.split('/demo/')[1]} />;
  }

  // Portail client — accessible sans auth principale
  if (currentPage === '__portal') {
    return <PortalClient />;
  }

  // Espace expert-comptable — authentification distincte (EXPERT_JWT_SECRET),
  // bypass complet de l'auth principale, cf. server/routes/expertAccess.js
  if (currentPage === '__expert-access') {
    return <ExpertAccess />;
  }

  // Superadmin — authentification distincte, bypass auth normale
  if (currentPage === '__superadmin') {
    return <Superadmin />;
  }

  // Pages légales — publiques, accessibles avec ou sans authentification
  // (URL directe /mentions-legales, /confidentialite, /cgu), dans leur
  // gabarit commun (components/LegalLayout.jsx). Depuis l'application, le
  // pied de page et le bandeau cookies ouvrent ces mêmes adresses dans un
  // nouvel onglet.
  if (currentPage === '__mentions-legales') {
    return <MentionsLegales />;
  }
  if (currentPage === '__confidentialite') {
    return <Privacy />;
  }
  if (currentPage === '__cgu') {
    return <Terms />;
  }

  // Mot de passe oublié — publiques, accessibles sans authentification.
  // Retour vers '__login' (pas '__landing') : ce sont des étapes du flux
  // de connexion, pas des pages informatives.
  if (currentPage === '__forgot-password') {
    return <ForgotPassword onGoToLogin={() => setCurrentPage('__login')} />;
  }
  if (currentPage === '__reset-password') {
    return <ResetPassword onGoToLogin={() => setCurrentPage('__login')} />;
  }

  // Landing page — accessible sans auth, et page par défaut du domaine nu.
  // Le "&& !user" est indispensable (même logique que __register plus bas) :
  // sans lui, un utilisateur déjà connecté qui retombe sur pilotpro360.com
  // resterait bloqué sur la landing au lieu de voir son tableau de bord.
  if (currentPage === '__landing' && !user) {
    return <Landing onGoToLogin={() => setCurrentPage('dashboard')} onGoToRegister={() => setCurrentPage('__register')} />;
  }

  // Inscription — accessible sans auth. Le "&& !user" est indispensable :
  // sans lui, une inscription réussie (user devient non-null) reste bloquée
  // sur cet écran puisque currentPage vaut toujours '__register'.
  if (currentPage === '__register' && !user) {
    return <Register onGoToLogin={() => setCurrentPage('dashboard')} />;
  }

  // Pas connecté → page de login
  if (!user) return <Login onLanding={() => setCurrentPage('__landing')} onGoToRegister={() => setCurrentPage('__register')} onGoToForgotPassword={() => setCurrentPage('__forgot-password')} />;

  // Nouvel utilisateur → questionnaire activité/priorités, puis wizard
  // Exclus : superadmins, comptes déjà complétés
  const needsOnboarding =
    !user.onboarding_completed &&
    !user.is_superadmin &&
    user.role !== 'superadmin';
  if (needsOnboarding) {
    // currentCompany doit être chargé avant de savoir si le questionnaire
    // a déjà été répondu (activity_type) — évite d'afficher le wizard par
    // défaut pendant la fraction de seconde de chargement.
    if (companiesLoading || !currentCompany) return null;
    if (!currentCompany.activity_type) {
      return <ActivityQuestionnaire company={currentCompany} onComplete={loadCompanies} />;
    }
    return <OnboardingWizard activityType={currentCompany.activity_type} onComplete={refreshUser} />;
  }

  // Priorités du questionnaire d'onboarding (partie 1) — pilotent le
  // regroupement de la sidebar (partie 3). [] pour toute société créée
  // avant ce changement ou n'ayant pas répondu : Sidebar affiche alors
  // toutes les sections normalement, sans regroupement (non régressif).
  let currentCompanyPriorities = [];
  try { currentCompanyPriorities = JSON.parse(currentCompany?.onboarding_priorities || '[]'); } catch {}

  const PageComponent = PAGES[currentPage]?.component || Dashboard;
  const pageLabel     = PAGES[currentPage]?.label     || "Tableau de bord";

  // Props transmis à chaque page
  const pageProps = {
    onNavigate:         setCurrentPage,
    currentSociete:     currentCompany?.name || "",
    currentCompanyId:   currentCompany?.id   || null,
    currentCompany:     currentCompany,
    companies:          companies,
    onCompaniesChange:  (updated) => {
      setCompanies(updated);
      if (updated.length > 0 && (!currentCompany || !updated.find(c => c.id === currentCompany.id))) {
        setCurrentCompany(updated[0]);
      }
    },
  };

  return (
    <>
    {user?.is_impersonation && (
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 99999,
        background: '#dc2626', color: '#fff', textAlign: 'center',
        padding: '8px 16px', fontSize: 13, fontWeight: 700,
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12,
        boxShadow: '0 2px 8px rgba(220,38,38,0.5)',
      }}>
        🔴 MODE IMPERSONATION SUPERADMIN — LECTURE SEULE — Session expire dans 30 min
        <span style={{ fontSize: 11, fontWeight: 400, opacity: 0.9 }}>
          ({user.email} / tenant #{user.tenant_id})
        </span>
      </div>
    )}
    <TrialBanner user={user} company={currentCompany} onNavigate={setCurrentPage} />
    <NewVersionBanner />
    <div className="app-root" style={user?.is_impersonation || user?.trial?.blocked ? { paddingTop: 36 } : {}}>
      <Sidebar
        currentPage={currentPage}
        onNavigate={setCurrentPage}
        open={sidebarOpen}
        companies={companies}
        currentCompany={currentCompany}
        onCompanyChange={handleCompanyChange}
        onLogout={logout}
        countBadges={countBadges}
        priorities={currentCompanyPriorities}
      />
      <div className={`app-main ${sidebarOpen ? "" : "sidebar-closed"}`}>
        <Topbar
          pageLabel={pageLabel}
          onMenuToggle={() => setSidebarOpen(o => !o)}
          currentSociete={currentCompany?.name || ""}
          onNavigate={setCurrentPage}
        />
        <div className="app-content">
          <DemoDataBanner user={user} companyId={currentCompany?.id} onPurged={() => setDataVersion(v => v + 1)} />
          {companiesLoading ? (
            <div style={{ textAlign: "center", padding: 60, color: "var(--text-muted)" }}>
              Chargement des sociétés…
            </div>
          ) : (
            <PageComponent key={dataVersion} {...pageProps} />
          )}
        </div>
        <Footer />
      </div>
    </div>
     <AIDrawer currentCompanyId={currentCompany?.id} />
    <CookieBanner />
    </>
  );
}

// ── Racine avec le provider d'auth ────────────────────────────
export default function App() {
  return (
    <ThemeProvider>
      <ToastProvider>
        <AuthProvider>
          <AIProvider>
            <AppContent />
          </AIProvider>
        </AuthProvider>
      </ToastProvider>
    </ThemeProvider>
  );
}